import { Copy, Mail, Phone, MapPin, Radio, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "@/components/PageHero";
import { FadeUp } from "@/components/motion";
import { useLang } from "@/context/LangContext";
import { PageLayout } from "@/components/PageLayout";

const EMAIL = "support@awainkelet.org";

export default function Contact() {
  const { t } = useLang();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(EMAIL);
      toast(t("contact.copied"));
    } catch {
      toast(EMAIL);
    }
  };
  const info = [
    [Phone, t("contact.phone"), t("contact.phoneValue"), "phone"],
    [MapPin, t("contact.address"), t("contact.addressValue"), "address"],
    [Radio, t("contact.freq"), t("contact.freqValue"), "frequency"],
  ];
  const phone = t("contact.phoneValue");
  const tel = phone.includes("[") ? "" : phone.replace(/[^\d+]/g, "");
  return (
    <main data-testid="contact-page">
      <PageLayout page="contact" sections={{
        hero: <PageHero testid="contact-hero" over={t("contact.over")} title={t("contact.title")} lead={t("contact.lead")} imgKey="contact_hero" />,
        email: (
      <section className="mx-auto max-w-[1400px] px-5 md:px-10">
        <FadeUp className="relative overflow-hidden rounded-[32px] border border-[#D4AF37]/25 bg-[#131C2E] p-7 md:p-14">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,rgba(212,175,55,0.25),transparent_70%)]" />
          <p className="relative flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4AF37]">
            <Mail className="h-4 w-4" /> {t("contact.email")}
          </p>
          <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="font-display relative mt-6 block text-[clamp(1.7rem,7.4vw,4.5rem)] font-semibold leading-tight text-[#F4EEE2] transition-colors hover:text-[#D4AF37]">
            {EMAIL}
          </a>
          <div className="relative mt-10 flex flex-wrap gap-3">
            <a href={`mailto:${EMAIL}`} data-testid="contact-write-btn" className="flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3.5 font-ui font-semibold text-[#0B111E] transition-transform hover:-translate-y-0.5">
              {t("contact.write")} <ArrowUpRight className="h-4 w-4" />
            </a>
            <button onClick={copy} data-testid="contact-copy-btn" className="flex items-center gap-2 rounded-full border border-[#F4EEE2]/20 px-6 py-3.5 font-ui font-semibold text-[#F4EEE2] transition-colors hover:border-[#D4AF37]">
              <Copy className="h-4 w-4" /> {t("contact.copy")}
            </button>
          </div>
        </FadeUp>
      </section>
        ),
        info: (
      <section className="mx-auto max-w-[1400px] px-5 pb-28 md:px-10">
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {info.map(([Icon, label, val, id], i) => (
            <FadeUp key={id} delay={i * 0.08} className="rounded-[24px] border border-[#F4EEE2]/10 p-7" data-testid={`contact-info-${id}`}>
              <Icon className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <p className="mt-6 font-ui text-xs uppercase tracking-[0.2em] text-[#B7BFCC]">{label}</p>
              {id === "phone" && tel ? (
                <a href={`tel:${tel}`} data-testid="contact-phone-call-link" className="mt-2 block font-display text-2xl text-[#F4EEE2] underline decoration-[#D4AF37]/50 underline-offset-4 transition-colors hover:text-[#D4AF37]">{val}</a>
              ) : (
                <p className="mt-2 font-display text-2xl text-[#F4EEE2]">{val}</p>
              )}
            </FadeUp>
          ))}
        </div>
      </section>
        ),
      }} />
    </main>
  );
}
